import { DateTime } from 'luxon';

/** Narrowest segment shape these helpers read — kept structural so any generated segment fragment satisfies it. */
type LegSplittableSegment = {
  /** Segment departure; `iataCode` detects the round-trip turnaround, `datetime` measures the ground gap. */
  departure?: { datetime?: string | null; iataCode?: string | null } | null;
  /** Segment arrival; `iataCode` detects the round-trip turnaround, `datetime` measures the ground gap. */
  arrival?: { datetime?: string | null; iataCode?: string | null } | null;
  /** Timezone-correct flight time (precomputed by the source), summed for a leg's elapsed duration. */
  durationInSeconds?: number | null;
};

/**
 * A native bundle can model a whole round trip as ONE itinerary whose final segment arrives back at the origin,
 * so the SPA would otherwise draw it as a single flight with a multi-day "stopover". It is really two flights.
 * Detect the return-to-origin case and split at the longest ground gap (the turnaround) — mirroring the backend's
 * destination derivation — so each real flight renders on its own. One-way itineraries (including genuine
 * multi-stop connections) are returned unchanged as a single leg.
 */
export function splitItineraryIntoLegs<T extends LegSplittableSegment>(segments: readonly T[]): T[][] {
  if (segments.length <= 1) {
    return [[...segments]];
  }

  const firstDeparture = segments[0]?.departure?.iataCode;
  const lastArrival = segments[segments.length - 1]?.arrival?.iataCode;
  if (!firstDeparture || firstDeparture !== lastArrival) {
    return [[...segments]];
  }

  let splitIndex = 0;
  let longestGapMs = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const gapMs =
      DateTime.fromISO(segments[i + 1]?.departure?.datetime ?? '').toMillis() -
      DateTime.fromISO(segments[i]?.arrival?.datetime ?? '').toMillis();
    if (gapMs > longestGapMs) {
      longestGapMs = gapMs;
      splitIndex = i;
    }
  }

  return [segments.slice(0, splitIndex + 1), segments.slice(splitIndex + 1)];
}

/**
 * Real elapsed duration of a leg in seconds: each segment's flight time plus the connection gaps between them.
 * Per-segment durations are timezone-correct, so we sum them rather than subtract naive local datetimes across
 * regions (which would lie for trips that cross timezones).
 */
export function getLegDurationSeconds(segments: readonly LegSplittableSegment[]): number {
  let total = 0;
  for (let i = 0; i < segments.length; i += 1) {
    total += segments[i]?.durationInSeconds ?? 0;
    if (i > 0) {
      const gapMs =
        DateTime.fromISO(segments[i]?.departure?.datetime ?? '').toMillis() -
        DateTime.fromISO(segments[i - 1]?.arrival?.datetime ?? '').toMillis();
      if (Number.isFinite(gapMs) && gapMs > 0) {
        total += gapMs / 1000;
      }
    }
  }
  return total;
}

/**
 * The segment whose arrival is the itinerary's true outbound destination — i.e. where the traveler is flying TO.
 * For a round trip in one itinerary that is the turnaround (end of the first leg), not the final arrival (which is
 * the origin). Used to label the destination instead of taking a naive last-segment arrival.
 */
export function getOutboundArrivalSegment<T extends LegSplittableSegment>(segments: readonly T[]): T | undefined {
  const [outbound] = splitItineraryIntoLegs(segments);
  return outbound[outbound.length - 1];
}

/** How many real flights a set of itineraries covers, since a round trip can arrive as a single itinerary. */
export function countItineraryLegs(
  itineraries: readonly { segments?: readonly LegSplittableSegment[] | null }[],
): number {
  return itineraries
    .flatMap((itinerary) => splitItineraryIntoLegs(itinerary.segments ?? []))
    .filter((leg) => leg.length > 0).length;
}
